// QR codes as SVG for the stickers, drawn from ZXing's encoder (the library the scanner already loads,
// same lazy chunk). Vector paths, one viewBox unit per module: sharp at any print size.
//
// Error correction Q (a quarter of the code may be dirty or scratched): the sticker URL still fits version 4,
// 33 × 33 modules. The SVG carries the 4-module quiet zone the standard asks for, so nothing printed next to it
// can come closer.
//
// ZXing's detector (0.23, the app's own scanner; web readers use it too) sometimes takes a finder-like spot in the
// data for one of the three corner squares and gives up: with the encoder's mask about 5 % of the sizes and
// positions tried failed, for a third of the codes. The 8 standard masks are 8 equally valid pictures of the same
// data, so a code is drawn with the first mask (best standard score first) that ZXing reads back in every one of
// CHECKS: that leaves about 0.5 %, which a second try (another binarizer, a turned picture) reads.
let zxing = null;
function loadEncoder() {
    if (!zxing) {
        zxing = import(/* webpackChunkName: "zxing" */ '@zxing/library');
        zxing.catch(() => {
            zxing = null; // a failed chunk download can be retried
        });
    }
    return zxing;
}

export const QUIET_MODULES = 4;
// The read-back test: 20 renders from 2.2 to 10 px per module, each at another sub-pixel position (pixel rounding
// changes what the detector sees). ~25 ms per code.
const CHECKS = Array.from({ length: 20 }, (_, i) => ({
    scale: 2.2 * Math.pow(10 / 2.2, i / 19),
    dx: ((i + 1) * 0.618034) % 1,
    dy: ((2 * i + 7) * 0.618034) % 1,
}));

// The data + error correction bits, as QRCodeEncoder.encode builds them (byte mode, UTF-8, no ECI)
function finalBits(Z, text, level, version) {
    const E = Z.QRCodeEncoder;
    const encoding = E.DEFAULT_BYTE_MODE_ENCODING;
    const mode = E.chooseMode(text, encoding);
    const data = new Z.BitArray();
    E.appendBytes(text, mode, data, encoding);
    const bits = new Z.BitArray();
    E.appendModeInfo(mode, bits);
    E.appendLengthInfo(mode === Z.QRCodeMode.BYTE ? data.getSizeInBytes() : text.length, version, mode, bits);
    bits.appendBitArray(data);
    const ecBlocks = version.getECBlocksForLevel(level);
    const numDataBytes = version.getTotalCodewords() - ecBlocks.getTotalECCodewords();
    E.terminateBits(numDataBytes, bits);
    return E.interleaveWithECBytes(bits, version.getTotalCodewords(), numDataBytes, ecBlocks.getNumBlocks());
}

function maskPenalty(Z, matrix) {
    const M = Z.QRCodeMaskUtil;
    return M.applyMaskPenaltyRule1(matrix) + M.applyMaskPenaltyRule2(matrix) + M.applyMaskPenaltyRule3(matrix) + M.applyMaskPenaltyRule4(matrix);
}

// The code with its quiet zone as grey levels, as a print or a photo shows it: `scale` px per module, the code's
// corner at (dx, dy) px, every pixel as dark as the part of it the dark modules cover.
function render(Z, matrix, { scale, dx, dy }) {
    const n = matrix.getWidth();
    const size = Math.ceil((n + 2 * QUIET_MODULES) * scale + Math.max(dx, dy)) + 1;
    const dark = (mx, my) => {
        const x = mx - QUIET_MODULES;
        const y = my - QUIET_MODULES;
        return x >= 0 && y >= 0 && x < n && y < n && matrix.get(x, y) === 1;
    };
    const lum = new Uint8ClampedArray(size * size);
    for (let py = 0; py < size; py++) {
        const y0 = (py - dy) / scale;
        const y1 = (py + 1 - dy) / scale;
        for (let px = 0; px < size; px++) {
            const x0 = (px - dx) / scale;
            const x1 = (px + 1 - dx) / scale;
            let cover = 0;
            for (let my = Math.floor(y0); my < y1; my++) {
                const h = Math.min(y1, my + 1) - Math.max(y0, my);
                for (let mx = Math.floor(x0); mx < x1; mx++) {
                    if (dark(mx, my)) cover += h * (Math.min(x1, mx + 1) - Math.max(x0, mx));
                }
            }
            lum[py * size + px] = Math.round(255 * (1 - cover * scale * scale));
        }
    }
    return new Z.RGBLuminanceSource(lum, size, size);
}

// how many CHECKS renders ZXing fails to read `text` back from (stops counting past `limit`)
function readFailures(Z, matrix, text, limit) {
    const reader = new Z.QRCodeReader();
    let failures = 0;
    for (const check of CHECKS) {
        let ok = false;
        try {
            ok = reader.decode(new Z.BinaryBitmap(new Z.HybridBinarizer(render(Z, matrix, check)))).getText() === text;
        } catch (e) {
            ok = false;
        } finally {
            reader.reset();
        }
        if (!ok && ++failures >= limit) break;
    }
    return failures;
}

/** The module matrix of the QR code for `text` (a ZXing ByteMatrix: getWidth(), get(x, y) === 1 for dark). */
export async function qrMatrix(text) {
    const Z = await loadEncoder();
    const level = Z.QRCodeDecoderErrorCorrectionLevel.Q;
    const value = String(text);
    const encoded = Z.QRCodeEncoder.encode(value, level);
    const version = encoded.getVersion();
    const bits = finalBits(Z, value, level, version);
    const size = version.getDimensionForVersion();
    const candidates = [];
    for (let mask = 0; mask < 8; mask++) {
        const matrix = new Z.QRCodeByteMatrix(size, size);
        Z.QRCodeMatrixUtil.buildMatrix(bits, level, version, mask, matrix);
        candidates.push({ matrix, penalty: maskPenalty(Z, matrix) });
    }
    candidates.sort((a, b) => a.penalty - b.penalty);
    let best = null;
    for (const candidate of candidates) {
        const failures = readFailures(Z, candidate.matrix, value, best ? best.failures : CHECKS.length);
        if (!failures) return candidate.matrix;
        if (!best || failures < best.failures) best = { matrix: candidate.matrix, failures };
    }
    return best.matrix; // no mask read back every time: the one that failed least
}

/**
 * SVG markup of the QR code for `text` with a quiet zone of `quiet` modules, and its width in modules
 * (quiet zone included). The SVG has no size: it fills its box (width/height 100%).
 * @returns {Promise<{svg: string, modules: number}>}
 */
export async function qrSvg(text, { quiet = QUIET_MODULES } = {}) {
    const matrix = await qrMatrix(text);
    const size = matrix.getWidth();
    let d = '';
    for (let y = 0; y < size; y++) {
        let x = 0;
        while (x < size) {
            if (matrix.get(x, y) !== 1) {
                x++;
                continue;
            }
            // one rectangle per run of dark modules
            let run = 1;
            while (x + run < size && matrix.get(x + run, y) === 1) run++;
            d += `M${x + quiet} ${y + quiet}h${run}v1h-${run}z`;
            x += run;
        }
    }
    const box = size + 2 * quiet;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${box} ${box}" shape-rendering="crispEdges" width="100%" height="100%"><rect width="${box}" height="${box}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
    return { svg, modules: box };
}
